<template lang="pug">
.users-page
	.top-bar
		h1.title {{ $route.name }}
		.button(@click='createUser') Create New User
	p.spacey(v-if='tableState === "failed"') Couldn't Load Users
	.button.error(
		v-if='tableState === "failed"'
		@click='tryAgain'
	) Try Again
	p.spacey(v-if='noUsers') No Users To Show
	.space-table-wrapper(v-if='!noUsers && tableState !== "failed"')
		SpaceTable.table(
			:search-components='searchComponents'
			:columns='columns',
			:rows='activeRows',
			:state='tableState',
			:noResults='noResults'
			@sort='sort'
			@search='query = $event'
		)
</template>

<script>
import SpaceTable from '@/components/SpaceTable';
import SimpleSearch from '@/components/SimpleSearch';
import SpaceForm from '@/components/SpaceForm';
import UserTableMenu from '@/components/UserTableMenu';

import { GetUsers } from '@/lib/db/users';
// eslint-disable-next-line no-unused-vars
import { SetDefault } from '@/lib/utils';
import Submit from '@/lib/Submit';
import BM25 from '@/lib/BM25';

export default {

	name : 'users-page',

	data : () => ( {

		searchComponents : {
			string : SimpleSearch,
		},

		query           : '',
		sortedColumn    : '',
		sortedDirection : 1,

		summary : [],

		filteredRows : {},

		columns : {
			name : {
				title      : 'Name',
				hidden     : false,
				searchable : 'string',
				order      : 0,
				sortType   : 'string',
				activeSort : false,
			},
			status : {
				title      : 'Status',
				hidden     : false,
				searchable : 'string',
				order      : 1,
				sortType   : 'string',
				activeSort : false,
			},
			email : {
				title      : 'Email',
				hidden     : false,
				searchable : 'string',
				order      : 2,
				sortType   : 'string',
				activeSort : false,
			},
			permissionLevel : {
				title      : 'Permissions',
				hidden     : false,
				searchable : 'string',
				order      : 3,
				sortType   : 'string',
				activeSort : false,
			},
			menu : {
				searchable : false,
				order      : 4,
				component  : UserTableMenu,
			}
			// object like
			// key : {
			//   title      : String,
			//   searchable : Boolean, when someone hits sort, SpaceTable will emit @search-[sortable] if defined
			//   sortType   : String,
			//   ...
			// },
			// ...
		},

		// array of objects which matches the structure
		// of the 'columns' object
		users : [],

		tableState : 'loading',

		gradientState : 'after',

		userIndex : null,

	} ),

	created() {

		this.getUsers();

	},

	computed : {

		// * Rows currently visible according
		// * to our search paramater
		activeRows() {
			// get the query and index. This will update
			// every time either of these change and thus
			// always be reactive to database changes
			const { query } = this;

			if ( !query ) {
				// return all the rows if there's no search
				// query
				return this.sortedRows;
			}

			const active   = this.userIndex.search( query );
			const isActive = active
				.map( a => a.uniqueKey )
				.reduce( ( obj, key ) => {
					const added = {};
					added[key]  = true;

					return Object.assign( obj, added );
				}, {} );

			// return sortedRows so that they're all in
			// order, and then filter out ones that are
			// not active
			return this.sortedRows.filter( row => isActive[row.uniqueKey] );
		},

		// * Rows sorted by the current column
		sortedRows() {
			const { users } = this;
			const { sortedColumn, sortedDirection } = this;

			// if we have no sortedColumn,
			// just return the rows
			if ( !sortedColumn ) {
				return users;
			}

			// sort by the desired column
			const sorted = users.sort( ( a, b ) => {
				const A = a[sortedColumn] || '';
				const B = b[sortedColumn] || '';

				// if the sortedDirection is -1,
				// we can reverse the sortedArray
				if ( sortedDirection < 0 ) {
					return B.localeCompare( A );
				}

				return A.localeCompare( B );
			} );

			return sorted;
		},

		noUsers() {

			if ( this.tableState === 'loaded' && this.users.length === 0 ) {
				return true;
			}

			return false;

		},

		noResults() {

			if ( this.query && this.users.length > 0 && this.activeRows.length <= 0 ) {
				return true;
			}

			return false;

		}

	},

	methods : {

		tryAgain() {
			this.getUsers();
		},

		getUsers() {

			GetUsers( ( users ) => {
				// get all users keys
				const uids = Object.keys( users );

				// set users and make sure all properties are available;
				this.users = uids.map( ( uid, i ) => {

					/* eslint-disable no-param-reassign */

					// * give them a guaranteed unique key * //
					users[uid].uniqueKey = `_${i}`;

					switch ( parseInt( users[uid].permissionLevel, 10 ) ) {

						case 1: {
							users[uid].permissionLevel = 'Read Only';
							break;
						}

						case 2: {
							users[uid].permissionLevel = 'Read and Write';
							break;
						}

						case 3: {
							users[uid].permissionLevel = 'Read, Write, and Admin';
							break;
						}

						default: {
							break;
						}
					}

					/* eslint-enable no-param-reassign */

					return users[uid];
				} );

				// update table state
				this.tableState = 'loaded';

				// build our user index
				const userIndex = new BM25();

				userIndex.index( this.users, {
					uniqueKey : 'uniqueKey'
				} );

				this.userIndex = userIndex;
			} );

		},

		createUser() {

			// create a user for the SpaceTable
			this.$store.dispatch( 'form', {
				type : 'user',
			} )
				.then( async ( form ) => {

					const stateIndicator = await this.$store.dispatch( 'displayState', {
						message : 'Creating User...',
						state   : 'loading'
					} );

					Submit.user( form )
						.then( form.close() )
						.then( () => stateIndicator.resolve( 'Created Successfully' ) )
						.catch( () => {
							form.close();
							stateIndicator.fail( { header : 'Didn\'t Create User', message : '' } );
						} );
				} );
		},

		sort( column ) {
			// first, we need to determine
			// which column is being sorted
			const { key } = column;

			// If we already sorted by this key,
			// we just flip the array
			if ( this.sortedColumn === key ) {
				this.sortedDirection *= -1;

				return;
			}

			// otherwise, we need to make a record
			// of which column is currently sorted
			this.sortedColumn    = key;
			this.sortedDirection = 1;
		},
	},

	components : {
		SpaceTable,
		SimpleSearch,
		SpaceForm,
	}

};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">

.users-page {
	width: 100%;
	max-width: 2000px;
	padding: 0 100px;
	margin: 0 auto;
	overflow: auto;

	p.spacey {
		text-align: center;
	}

	.button.error {
		margin: 15px auto;
	}

	.top-bar {
		width: 100%;
		display: flex;
		justify-content: space-between;
		flex-flow: row;
		padding: 35px 0;

		.button {
			white-space: nowrap;
			width: auto;
			border-radius: 30px;
			box-shadow: none;
			transition: .2s box-shadow ease;

			&:hover {
				box-shadow: $defaultBoxShadow;
			}
		}
	}

	// space-table default css
	.space-table-wrapper {
		width: 100%;
		position: relative;

		.space-table {
			text-align: left;
			font-size: 12px!important;
			border-radius: 5px;
			overflow-y: initial;
			font-family: 'Poppins', sans-serif;

			.table-wrapper {
				overflow-x: auto;
				padding-bottom : 150px;

				.simple-search {
					position: relative;
					transform: none;
					flex: 0 0 100%;
					top: 0;
					left: 0;
					margin-bottom: 15px;

					.triangle {
						display: none;
					}

					.main-search {
						width: 250px;
						background: $color-primary;
						box-shadow: none;
						border-radius: 5px;

						&:before {
							display: none;
						}

						input {
							font-family: 'Poppins', sans-serif;
							font-style: italic;
							font-weight: 300;
						}

						.icon-close {
							display: none;
						}
					}
				}

				table{
					table-layout: fixed;
					width: 100%;

					tr{
						background-color: white!important;
						position: relative;

						&:hover {
							background-color: white!important;
							cursor: pointer;

							>.cell>.dot-container {
								pointer-events: all;

								.icon-more_menu {
									opacity: 1;
									transition: .2s ease-in-out;
								}

								.menu {
									opacity:0;
								}
							}
						}

						&:nth-child(1),&:nth-child(2) {
							box-shadow: none;
						}

						&:nth-child(1) {

							td {

								&:last-of-type {
									pointer-events: none;
								}
							}
						}

						td {
							padding: 16px 0;

							&.header {

								&::before,
								&::after {
									right: 15px;
									left: unset !important;
								}
							}

							&:nth-last-child(1) {
								position: relative;
								width: 50px;

								&.header {
									opacity: 0;
								}
							}

							&:nth-child(2) {
								width: 15%;
							}

							&:nth-child(3) {
								width: 35%;
							}

							.sub-header {
								display: none;
							}
						}

						.header {
							padding: 13px 0!important;
							font-family: inherit!important;

							p {
								font-weight: 500;
								color: $blueGrey;
							}
						}

						&.row.clickable {
							border-bottom: 1px solid rgba(80, 96, 110, 0.17);
						}

						.dot-container {
							position: absolute;
							top: 0;
							right: 15px;
							padding: 14px 20px;
							border-radius: 15px;
							color: color-primary;
							opacity: 1;
							min-width: 190px;
							min-height: 130px;

							&.active {
								background-color: white;
								box-shadow: $defaultBoxShadow;
								z-index: 3;

								.icon-more_menu {
									pointer-events: none;
								}

								.menu {
									opacity: 1!important;
									z-index: 2;
									pointer-events: all;
								}
							}

							&:hover {
								cursor: default;
							}

							.icon-more_menu {
								font-size: 20px;
								position: relative;
								display: block;
								float: right;
								text-align: right;
								z-index: 2;
								padding: 0 5px;
								margin-top: 1px;
								opacity: 0;

								&:hover {
									cursor: pointer;
								}
							}

							.title {
								font-size: 12px;
								color: $color-primary;
								font-weight: 600;
								margin: 0;
								margin-bottom: 8%;
							}

							.menu {
								opacity:0;
								pointer-events: none;
								z-index: -1;

								.menu-option {
									margin: 4% 0;
									color: color-primary;

									&:hover {
										cursor: pointer;
									}
								}
							}
						}
					}
				}

				.no-rows {
					margin-top: 150px;
				}

				.pagination {

					.pagination-wrapper {
						display: flex;
						justify-content: space-between;
						width: 100%;
						padding-right: 25px;

						.indicator {

							p {
								font-size: 16px;
							}
						}
					}
				}
			}
		}
	}

	.table {
		overflow-y: initial;

		.table-wrapper {
			overflow-x: scroll;
			padding-bottom : 150px;

			.simple-search {

				.main-search {
					width: 100%!important;
					background: $lightGrey!important;
					color: $blueGrey;
				}
			}

			table {
				display: table;
				margin-top: 35px;

				tr {
					background: white;

					&:nth-child(1), &:nth-child(2) {
						box-shadow: none;
					}

					&:hover {
						background-color: white!important;

						>.cell>.dot-container {
							pointer-events: all;

							.icon-more_menu {
								opacity: 1;
							}

							.menu {
								opacity:0;
							}
						}
					}

					&.row.clickable {
						border-bottom: 1px solid rgba(80, 96, 110, 0.17);
					}

					td {
						padding: 12px 0;

						&:nth-last-child(1) {
							position: relative;
							min-width: 50px;
						}

						&.header {

							&.active {

								&::before, &::after {
									opacity: 1!important;
								}
							}

							&.icon-arrow {

								&:hover {

									&::before, &::after {
										opacity: 1;
									}
								}

								&::before, &::after {
									background: $input-color;
									opacity: 0;
									transition: .2s ease-in-out;
									top: 45%;
									left: 60%;
								}

								&::before {
									transform: translate(-50%, -50%) rotate(135deg);
								}

								&::after {
									transform: translate(1px, -50%) rotate(45deg);
								}


								&.down {

									&::before {
										transform: translate(-50%, -50%) rotate(45deg);
									}

									&::after {
										transform: translate(1px, -50%) rotate(135deg);
									}
								}
							}

							p.header {
								font-size: 16px!important;
							}
						}

						p {
							color: $blueGrey;
						}

						.dot-container {

							.title {
								font-size: 14px;
							}

							.menu-option {
								font-size: 14px;
							}
						}
					}
				}
			}
		}
	}
}

</style>
